/**
 * FinanceOpsCard — Finance Operations Health showing SO→Invoice timeline,
 * pending invoicing aging, and collection metrics. Finance team view.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { targetAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { DollarSign, Clock, AlertTriangle, FileText, Users, TrendingDown } from 'lucide-react';
import { buildHealthCardParams } from './healthCardParams';
import { HealthFilterScopeRibbon } from './HealthFilterScopeRibbon';

const TABS = [
  { key: 'overview', label: 'Overview', icon: DollarSign },
  { key: 'pending', label: 'Pending SOs', icon: Clock },
  { key: 'by_rep', label: 'By Rep', icon: Users },
];

const fmt = (v) => {
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
  return v?.toFixed?.(0) ?? v;
};

const AGING_LABELS = { '0_30': '0-30 days', '31_60': '31-60 days', '61_90': '61-90 days', '90_plus': '90+ days' };
const AGING_COLORS = { '0_30': '#22c55e', '31_60': '#eab308', '61_90': '#f97316', '90_plus': '#ef4444' };

export function FinanceOpsCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    targetAPI.getFinanceOperations(requestParams)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  if (loading) return <div className="p-4 space-y-3"><Skeleton className="h-8 w-48" /><Skeleton className="h-32" /></div>;
  if (!data) return <div className="p-4 text-gray-400 text-sm">No data available</div>;

  const { summary, pending_aging, pending_details, by_salesperson } = data;

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border overflow-hidden" data-testid="finance-ops-card">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <h3 className="text-sm font-bold">Finance Operations</h3>
          </div>
          <Badge className={`text-[10px] border-0 ${summary?.invoicing_rate > 70 ? 'bg-green-400/30 text-white' : 'bg-red-400/30 text-white'}`}>
            {summary?.invoicing_rate}% invoiced
          </Badge>
        </div>
        <div className="mt-2"><HealthFilterScopeRibbon filters={filters} /></div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <div><p className="text-[10px] text-white/70">Total SOs</p><p className="text-sm font-bold">{summary?.total_so}</p></div>
          <div><p className="text-[10px] text-white/70">Pending</p><p className="text-sm font-bold text-red-200">{summary?.pending_count}</p></div>
          <div><p className="text-[10px] text-white/70">Pending Value</p><p className="text-sm font-bold text-red-200">{fmt(summary?.pending_value)}</p></div>
          <div><p className="text-[10px] text-white/70">Avg Collection</p><p className="text-sm font-bold">{summary?.avg_collection_days}d</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b px-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-all ${tab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 min-h-0">
        {tab === 'overview' && (
          <div className="space-y-3">
            {/* Aging breakdown */}
            <div>
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-2">Pending Invoice Aging</p>
              <div className="space-y-1.5">
                {Object.entries(pending_aging || {}).map(([key, val]) => {
                  const maxAmt = Math.max(...Object.values(pending_aging || {}).map(v => v.amount || 0), 1);
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-16 shrink-0">{AGING_LABELS[key]}</span>
                      <div className="flex-1 bg-gray-50 rounded-full h-4 overflow-hidden">
                        <div className="h-full rounded-full transition-all flex items-center px-1.5"
                          style={{ width: `${Math.max(5, (val.amount / maxAmt) * 100)}%`, backgroundColor: AGING_COLORS[key] }}>
                          <span className="text-[9px] font-bold text-white">{val.count}</span>
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold text-gray-600 w-14 text-right">{fmt(val.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary metrics */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-emerald-50 p-2">
                <p className="text-[10px] text-emerald-600 font-medium">Invoiced</p>
                <p className="text-sm font-bold text-emerald-700">{summary?.invoiced_count} SOs</p>
                <p className="text-[10px] text-emerald-500">{fmt(summary?.invoiced_value)}</p>
              </div>
              <div className="rounded-lg bg-red-50 p-2">
                <p className="text-[10px] text-red-600 font-medium">Unpaid Invoices</p>
                <p className="text-sm font-bold text-red-700">{summary?.total_unpaid_invoices}</p>
                <p className="text-[10px] text-red-500">Avg {summary?.avg_collection_days}d to collect</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'pending' && (
          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-2">
              SOs Awaiting Invoice ({pending_details?.length || 0})
            </p>
            {(pending_details || []).map((so, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 border-l-2"
                style={{ borderLeftColor: so.days_pending > 90 ? '#ef4444' : so.days_pending > 60 ? '#f97316' : so.days_pending > 30 ? '#eab308' : '#22c55e' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{so.name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{so.customer} | {so.salesperson}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-gray-700">{fmt(so.amount)}</p>
                  <p className={`text-[10px] font-medium ${so.days_pending > 60 ? 'text-red-500' : 'text-gray-400'}`}>
                    {so.days_pending}d ago
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'by_rep' && (
          <div className="space-y-1.5">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-2">Pending by Salesperson</p>
            {(by_salesperson || []).map(sp => {
              const maxAmt = Math.max(...(by_salesperson || []).map(s => s.amount || 0), 1);
              return (
                <div key={sp.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{sp.name}</p>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div className="h-1.5 rounded-full bg-red-400 transition-all" style={{ width: `${(sp.amount / maxAmt) * 100}%` }} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-red-600">{fmt(sp.amount)}</p>
                    <p className="text-[10px] text-gray-400">{sp.count} SOs</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
